import { useFullAccess } from "../../billing/access";
import { t } from "../../i18n";
import { useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ReportSectionIds, type ReportSectionId, type ReportSettings } from "@tasduvar/engine";
import { useProjectStore } from "../../state/projectStore";
import { switchFromUrl } from "../../state/unsavedGuard";
import { useProvider } from "../../provider/useProvider";
import { useFeature, FeatureGate } from "../../provider/features";
import { loadAnyProject, projectFileName } from "../../storage";
import { useAuth } from "../../auth/useAuth";
import { buildReportModel } from "./model";
import { PRESETS, PRESET_LABEL, applyPreset, detectPreset } from "./presets";
import { Report } from "./Report";
import { printReport, PRINT_TIP } from "../../export/print";
import { useReportExports } from "./exports";
import { downloadBlob } from "../../lib/download";
import { useEffect } from "react";
import { useCadUi } from "../../state/cadUiStore";
import { CadShell } from "../cad/CadShell";
import { Palette } from "../cad/Palette";
import { LayoutTabs } from "../cad/LayoutTabs";
import { CadFormContext } from "../../ui/formContext";

export const SECTION_LABEL: Record<ReportSectionId, string> = {
  cover: "Kapak", projectInfo: "Proje ve duvar bilgileri", inputs: "Girdi tabloları", drawing: "Kesit çizimi", forceDiagram: "Kuvvet şeması + basınç diyagramı",
  seismicParams: "Deprem parametreleri", coefficients: "Basınç katsayıları", forces: "Yük tablosu", steps: "Hesap adımları (formüller)",
  stabilityTbdy: "Yönetmelik stabilite kontrolleri (TBDY / EC7 / ABD)", stabilityClassic: "Klasik güvenlik katsayıları", basePressure: "Dış merkezlilik ve taban gerilmeleri",
  bearingEc8: "EN 1998-5 Ek F sismik taşıma gücü (yöntem seçiliyse)",
  joints: "İç kesit kontrolleri", rcDesign: "Betonarme kesit tasarımı (betonarme duvarda)", reinforcement: "Donatı planı ve şeması (betonarme duvarda)", globalStability: "Toptan göçme (şev stabilitesi)", quantities: "Metraj ve keşif", warnings: "Uyarılar ve kabuller", summary: "Sonuç ve değerlendirme", references: "Kaynaklar",
};

/** `share` verilirse paylaşım görünümü: geri bağlantı paylaşıma döner, indirme izni bağlantıya bağlıdır. */
export function ReportPage({ share }: { share?: { token: string; allowDownload: boolean } } = {}): JSX.Element {
  const { id } = useParams();
  const nav = useNavigate();
  const { project, openProject, updateReportSettings, origin } = useProjectStore();
  const { status, subscription } = useAuth();
  const providerBranding = useProvider((s) => s.settings.branding);
  // Deneme sürümünde rapor filigranı sunucudan gelir; paylaşım görünümünde uygulanmaz
  const branding = useMemo(() => (subscription?.watermark && origin !== "shared" ? { ...providerBranding, watermark: subscription.watermark } : providerBranding), [providerBranding, subscription?.watermark, origin]);
  const exportAllowed = share ? share.allowDownload : !(origin === "cloud" && subscription && !subscription.canExport);
  const full = useFullAccess();   // ücretsiz katmanda rapor (hesap adımları) hiç çizilmez
  const imagePdf = useProvider((s) => s.settings.imagePdf);
  const appendLog = useProvider((s) => s.appendLog);
  const classicOn = useFeature("classicTable"), jointsOn = useFeature("jointChecks");
  const [tip, setTip] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [pdfErr, setPdfErr] = useState<string | null>(null);
  const setRibbonTab = useCadUi((s) => s.setRibbonTab);
  useEffect(() => { setRibbonTab("report"); }, [setRibbonTab]);   // rapor paftasında şerit Rapor sekmesine geçer
  useEffect(() => {
    if (project?.id === id || share || status === "unknown" || status === "guest") return;
    const load = (): void => void loadAnyProject(id ?? "", status === "authed").then((r) => { if (r) openProject(r.project, r.origin); else nav("/"); });
    // Başka proje açıkken (geri/ileri ya da elle adres) önce değişiklik sorulur; İptal → adres açık projeye döner
    const cur = project?.id;
    if (cur) switchFromUrl(load, () => nav(`/proje/${cur}/rapor`, { replace: true })); else load();
  }, [id, project?.id, openProject, nav, share, status]);
  const settings: ReportSettings | null = useMemo(() => {
    if (!project) return null;
    const s = project.reportSettings;
    // kapalı özelliklerin bölümleri rapora girmez
    return { ...s, sections: { ...s.sections, stabilityClassic: s.sections.stabilityClassic && classicOn, joints: s.sections.joints && jointsOn } };
  }, [project, classicOn, jointsOn]);
  const model = useMemo(() => (project && settings ? buildReportModel(project, settings, branding) : null), [project, settings, branding]);
  const log = (format: "pdf" | "pdf-image" | "docx" | "dxf" | "json" | "xlsx" | "csv"): Promise<void> => (project && model ? appendLog({ projectName: project.info.name, wallCount: model.walls.length, format, projectId: project.id }) : Promise.resolve());
  const exp = useReportExports(model, log);
  const dxfOn = useFeature("dxf"), docxOn = useFeature("docx");
  // Şerit Rapor sekmesi (PDF / Word / DXF / XLSX / CSV) → istek burada yerine getirilir; izin yoksa ya da özellik kapalıysa yok sayılır
  const request = useCadUi((s) => s.exportRequest), clearRequest = useCadUi((s) => s.setExportRequest);
  useEffect(() => {
    if (!request || !project || !model) return;
    clearRequest(null);
    if (!exportAllowed || !full) return;
    if (request === "pdf") {
      if (!imagePdf) { setTip(true); void log("pdf"); printReport(); return; }
      const pages = Array.from(document.querySelectorAll<HTMLElement>("#report-preview .report-page"));
      setProgress("Hazırlanıyor…"); setPdfErr(null);
      void import("../../export/imagePdf").then(({ exportImagePdf }) => exportImagePdf(pages, (d, n) => setProgress(`Sayfa ${d}/${n}`))).then((blob) => { downloadBlob(projectFileName(project).replace(/\.tasduvar\.json$/, "-rapor.pdf"), blob); return log("pdf-image"); })
        .catch((e: unknown) => setPdfErr(e instanceof Error ? e.message : "PDF üretilemedi")).finally(() => setProgress(null));
    } else if (request === "docx") { if (docxOn) void exp.docx(); }
    else if (request === "dxf") { if (dxfOn) exp.dxf(); }
    else if (request === "xlsx") void exp.xlsx();
    else if (request === "csv") exp.csv();
  }, [request]);
  if (status === "guest" && !share) return <Navigate to="/giris" replace />;   // API'li sürümde yalnız üyelere
  if (!project || !settings || !model) return <p className="p-6 text-stone">{t("Yükleniyor…")}</p>;

  const s = project.reportSettings;
  const setSections = (patch: Partial<Record<ReportSectionId, boolean>>): void => { const sections = { ...s.sections, ...patch }; updateReportSettings({ ...s, sections, preset: detectPreset(sections) }); };

  const back = share ? `/paylasim/${share.token}` : `/proje/${project.id}`;
  const gated = (sid: ReportSectionId): boolean => (sid === "stabilityClassic" && !classicOn) || (sid === "joints" && !jointsOn);
  return (
    <CadShell current="project" share={share}>
      <Palette as="aside" title={t("Rapor ayarları")} id="cad-report-settings" className="no-print shrink-0 border-r border-cad-line cad-report-settings" style={{ width: 268 }}>
        <fieldset>
          <legend>{t("Hazır ayar")}</legend>
          {(Object.keys(PRESETS) as (keyof typeof PRESETS)[]).map((k) => (
            <label key={k}><input type="radio" name="preset" checked={s.preset === k} onChange={() => updateReportSettings(applyPreset(s, k))} />{PRESET_LABEL[k]}</label>
          ))}
          {s.preset === "ozel" ? <span className="text-[10px] text-cad-ink3">{t("Özel (elle seçim)")}</span> : null}
        </fieldset>
        <fieldset>
          <legend>{t("Bölümler")}</legend>
          {ReportSectionIds.map((sid) => {
            const box = <label className="items-start"><input type="checkbox" className="mt-0.5" checked={s.sections[sid] ?? false} disabled={gated(sid)} onChange={(e) => setSections({ [sid]: e.target.checked })} />{SECTION_LABEL[sid]}</label>;
            return gated(sid) ? <FeatureGate key={sid} feature={sid === "joints" ? "jointChecks" : "classicTable"}>{box}</FeatureGate> : <div key={sid}>{box}</div>;
          })}
        </fieldset>
        <fieldset>
          <legend>{t("Duvarlar")}</legend>
          <label><input type="checkbox" checked={s.wallIds === "all"} onChange={(e) => updateReportSettings({ ...s, wallIds: e.target.checked ? "all" : project.walls.map((w) => w.id) })} />{t("Tümü")}</label>
          {project.walls.map((w) => (
            <label key={w.id} className="pl-3.5"><input type="checkbox" checked={s.wallIds === "all" || s.wallIds.includes(w.id)} disabled={s.wallIds === "all"} onChange={(e) => { const cur = s.wallIds === "all" ? project.walls.map((x) => x.id) : s.wallIds; updateReportSettings({ ...s, wallIds: e.target.checked ? [...cur, w.id] : cur.filter((x) => x !== w.id) }); }} />{w.name}</label>
          ))}
        </fieldset>
      </Palette>
      <div className="flex-1 flex flex-col min-w-0 min-h-0 cad-report-col">
        {/* Pafta araç çubuğu (28 px): geri · başlık · durum (çıktı düğmeleri şeridin Rapor sekmesinde) */}
        <CadFormContext.Provider value={true}>
          <div className="no-print shrink-0 flex items-center gap-1.5 h-7 px-2 bg-cad-chrome1 border-b border-cad-line text-[11px]">
            <Link to={back} className="cad-btn-box">{share ? t("← Paylaşım") : t("← Model")}</Link>
            <h1 className="font-medium text-cad-ink2 truncate">{project.info.name} {t("· Rapor")}</h1>
            <span className="ml-auto" />
            {!exportAllowed ? <span className="text-[#B42318]" role="status">{share ? t("Bu bağlantıda indirme kapalı") : <>{t("Dışa aktarma için")} <Link to="/planlar" className="underline">{t("plan seçin")}</Link></>}</span> : null}
            {progress ? <span role="status" className="font-mono text-cad-ink3">{progress}</span> : null}
            {pdfErr ? <span role="alert" className="text-[#B42318]">{pdfErr}</span> : null}
            {exp.busy ? <span role="status" className="font-mono text-cad-ink3">{exp.busy}</span> : null}
            {exp.err ? <span role="alert" className="text-[#B42318]">{exp.err}</span> : null}
            {exportAllowed && full ? <span className="text-cad-ink3">{t("Çıktılar: şerit › Rapor")}</span> : null}
          </div>
        </CadFormContext.Provider>
        {tip ? <p role="status" className="no-print shrink-0 px-3 py-1 bg-cad-warnbg text-cad-warnfg text-[11px] border-b border-cad-line">{PRINT_TIP} <button type="button" className="underline ml-2 cursor-pointer" onClick={() => setTip(false)}>{t("Kapat")}</button></p> : null}
        {/* Koyu masa + beyaz kâğıt (README §3 "Rapor paftası"); kâğıt tipografisi report.css'ten, kabuk sızmaz */}
        <div className="cad-desk flex-1 overflow-auto bg-[#3A3F45] p-5" id="report-preview">{full ? <Report model={model} /> : (
          <div className="mx-auto max-w-[520px] mt-10 bg-white border border-cad-strong p-5 grid gap-3 text-[13px] text-cad-ink" role="note" aria-label={t("Rapor aboneliğe dahil")}>
            <h2 className="text-[16px] font-semibold">🔒 {t("Hesap raporu aboneliğe dahildir")}</h2>
            <p>{t("Ücretsiz hesapta duvarınızın kontrolleri sağlayıp sağlamadığını ve güvenlik sayılarını model ekranında görürsünüz. Adım adım hesap, kuvvet tablosu, donatı, metraj ve PDF / Word / DXF / XLSX çıktıları aboneliğe dahildir.")}</p>
            <div className="flex gap-2"><Link to="/planlar" className="cad-btn-primary">{t("Planları gör")}</Link><Link to={back} className="cad-btn-box">{t("← Model")}</Link></div>
          </div>
        )}</div>
        {share ? null : <LayoutTabs current="report" />}
      </div>
    </CadShell>
  );
}
